import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'
import circlePlaceholder from '../assets/circle-placeholder.svg'
import { TeamLogoSchema } from '../assets/api'
import fallbackPlaceholder from '../assets/fallbackPlaceholder.svg'

const getAllTeams = async ({
  competition_id,
  page,
  page_size,
}: {
  competition_id: number
  page?: number
  page_size?: number
}) => {
  const params: Record<string, number> = {}
  if (page !== undefined) params.page = page
  if (page_size !== undefined) params.page_size = page_size
  const response = await axios.get(
    `/api/competitions/${competition_id}/teams`,
    {
      params,
    }
  )
  return TeamListResponseSchema.parse(response.data)
}

export const useAllTeams = ({
  competition_id,
  page,
  page_size,
}: {
  competition_id: number
  page?: number
  page_size?: number
}) => {
  return useSuspenseQuery({
    queryKey: ['allTeams', { competition_id, page, page_size }],
    queryFn: () => getAllTeams({ competition_id, page, page_size }),
  })
}

const getTeam = async ({
  team_id,
  competition_id,
}: {
  team_id: number
  competition_id: number
}) => {
  const response = await axios.get(
    `/api/competitions/${competition_id}/teams/${team_id}`
  )
  return TeamDetailsSchema.parse(response.data)
}

export const useTeam = ({
  team_id,
  competition_id,
}: {
  team_id: number
  competition_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['team', { team_id, competition_id }],
    queryFn: () => getTeam({ team_id, competition_id }),
  })
}

const getTeamPlayers = async ({
  team_id,
  competition_id,
}: {
  team_id: number
  competition_id: number
}) => {
  const response = await axios.get(
    `/api/competitions/${competition_id}/teams/${team_id}/players`
  )
  return z.array(TeamPlayerSchema).parse(response.data)
}

export const useTeamPlayers = ({
  team_id,
  competition_id,
}: {
  team_id: number
  competition_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['teamPlayers', { team_id, competition_id }],
    queryFn: () => getTeamPlayers({ team_id, competition_id }),
  })
}

const getTeamStats = async ({
  team_id,
  competition_id,
}: {
  team_id: number
  competition_id: number
}) => {
  const response = await axios.get(
    `/api/competitions/${competition_id}/teams/${team_id}/stats`
  )
  return TeamStatsSchema.parse(response.data)
}

export const useTeamStats = ({
  team_id,
  competition_id,
}: {
  team_id: number
  competition_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['teamStats', { team_id, competition_id }],
    queryFn: () => getTeamStats({ team_id, competition_id }),
  })
}

export const TeamCaptainSchema = z.object({
  name: z.string(),
  main_photo_url: z
    .string()
    .url()
    .default(fallbackPlaceholder)
    .catch(fallbackPlaceholder),
})

export const TeamListSchema = z.object({
  team_id: z.number(),
  name: z.string(),
  location: z.string().optional(),
  logo: TeamLogoSchema,
})

const TeamListResponseSchema = z.object({
  items: z.array(TeamListSchema),
  total: z.number(),
})

export const TeamDetailsSchema = z.object({
  team_id: z.number(),
  name: z.string(),
  location: z.string().optional(),
  logo: TeamLogoSchema,
  player_count: z.number(),
  captain: TeamCaptainSchema.nullable(),
})

export const TeamPlayerSchema = z.object({
  player_id: z.number(),
  user_id: z.number().nullable(),
  name: z.string(),
  photo: z.object({
    main: z
      .string()
      .url()
      .optional()
      .default(circlePlaceholder)
      .catch(circlePlaceholder),
    'photo-32': z
      .string()
      .url()
      .optional()
      .default(circlePlaceholder)
      .catch(circlePlaceholder),
    'photo-48': z
      .string()
      .url()
      .optional()
      .default(circlePlaceholder)
      .catch(circlePlaceholder),
    'photo-84': z
      .string()
      .url()
      .optional()
      .default(circlePlaceholder)
      .catch(circlePlaceholder),
  }),
})

export const LastFiveMatchesSchema = z.array(z.enum(['win', 'draw', 'loss']))

export const TeamStatsSchema = z.object({
  team_id: z.number(),
  team_name: z.string(),
  team_logo: TeamLogoSchema,
  total_matches: z.number(),
  rank: z.number().nullable(),
  rank_change: z.number(),
  division_rank: z.number().nullable(),
  division_rank_change: z.number(),
  matches_won: z.number(),
  matches_draw: z.number(),
  matches_lost: z.number(),
  goals_for: z.number(),
  goals_against: z.number(),
  goal_difference: z.number(),
  last_5_matches: LastFiveMatchesSchema,
  points: z.number(),
})

export const useTeamPlayersOptional = ({
  team_id,
  competition_id,
  enabled,
}: {
  team_id: number
  competition_id: number
  enabled: boolean
}) => {
  return useQuery({
    queryKey: ['teamPlayers', { team_id, competition_id }],
    queryFn: () => getTeamPlayers({ team_id, competition_id }),
    enabled,
  })
}

export type TeamStats = z.infer<typeof TeamStatsSchema>
export type LastFiveMatches = z.infer<typeof LastFiveMatchesSchema>
export type TeamList = z.infer<typeof TeamListSchema>
export type TeamListResponse = z.infer<typeof TeamListResponseSchema>
export type TeamDetails = z.infer<typeof TeamDetailsSchema>
export type TeamCaptain = z.infer<typeof TeamCaptainSchema>
export type TeamPlayer = z.infer<typeof TeamPlayerSchema>
